import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { DeleteDashboard } from '~/components/SavedDashboards/DeleteDashboard/DeleteDashboard';
import { RenameDashboard } from '~/components/SavedDashboards/RenameDashboard/RenameDashboard';
import { ShareDashboard } from '~/components/SavedDashboards/ShareDashboard/ShareDashboard';
import { trackSegment } from '~/components/SegmentAnalytics';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { PersistedTable } from '~/components/Shared/PersistedTable/PersistedTable';
import type { FilterInstance } from '~/components/Shared/TableToggleFilter/TableToggleFilter';
import { TableToggleFilter } from '~/components/Shared/TableToggleFilter/TableToggleFilter';
import { User } from '~/components/Shared/User/User';
import { EmptyPage } from '~/eds/src/components/EmptyPage/EmptyPage';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Link } from '~/eds/src/components/Link/Link';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { SavedDashboardsActions } from '~/enums/SavedDashboards';
import { TableContextType } from '~/enums/TableContextType';
import { UserRoleType } from '~/enums/UserRoleType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useTableSettings } from '~/hooks/useTableSettings';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { api } from '~/redux/api/createApi';
import {
  DashboardTypesQueryParam,
  SavedDashboard,
} from '~/redux/api/methods/dashboardV2';
import { slices } from '~/redux/slices';
import { ERROR } from '~/types/toast.types';
import { DEFAULT_TABLE_SORT_COLUMN_ORDER } from '~/utils/table';

const Page = memo(() => {
  const intl = useIntl();
  const formatDate = useFormatDate();
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState(DEFAULT_TABLE_SORT_COLUMN_ORDER);
  const [typesParam, setTypesParam] = useState<DashboardTypesQueryParam[]>([]);
  const user = useCurrentUser();
  const { tableSettings } = useTableSettings(TableContextType.SavedDashboards);
  const hasLocalization = useFlag(FlagType.LanguageSelect);
  useEffect(() => {
    if (tableSettings) {
      setPageSize(tableSettings.pageSize);
      setSortBy(tableSettings.sortBy);
    }
  }, [tableSettings]);

  const handleUpdate = (state: any, action?: any) => {
    if (action?.type === 'toggleSortBy') {
      const updatedSortBy = state.sortBy[0];

      setSortBy(updatedSortBy);
    }
  };

  const isCreatorOrAdmin = (dashboard: SavedDashboard) =>
    dashboard.creator_id === user.id || user.role === UserRoleType.Admin;

  const { data, isFetching, isError } = api.endpoints.getDashboardsV2.useQuery({
    page,
    pageSize,
    sortBy,
    types: typesParam,
  });

  const filterOptions: FilterInstance<DashboardTypesQueryParam>[] = [
    {
      text: intl.formatMessage({
        id: 'SavedDashboards.mySavedDashboards',
        defaultMessage: 'My Saved Dashboards',
        description: "Filter option for user's own saved dashboards",
      }),
      value: 'mine',
    },
    {
      text: intl.formatMessage({
        id: 'SavedDashboards.sharedWithMe',
        defaultMessage: 'Shared with me',
        description: 'Filter option for dashboards shared with the user',
      }),
      value: 'shared',
    },
  ];

  const columns = useMemo(
    () => [
      {
        key: 'name',
        title: intl.formatMessage({
          id: 'SavedDashboards.nameColumn',
          defaultMessage: 'Name',
          description: 'Table column header for dashboard name',
        }),
        renderCell: (dashboard: SavedDashboard) => {
          return (
            <Link pathname={`/dashboards/${dashboard.id}`}>
              {dashboard.name}
            </Link>
          );
        },
      },
      {
        key: 'created_by',
        title: intl.formatMessage({
          id: 'SavedDashboards.createdByColumn',
          defaultMessage: 'Created by',
          description: 'Table column header for dashboard creator',
        }),
        disableSortBy: true,
        renderCell: (dashboard: SavedDashboard) => {
          return <User mode="avatar-name" id={dashboard.creator_id} />;
        },
      },
      {
        key: 'created_date',
        title: intl.formatMessage({
          id: 'SavedDashboards.createdOnColumn',
          defaultMessage: 'Created on date',
          description: 'Table column header for dashboard creation date',
        }),
        mapCellProps: (savedDashboard: SavedDashboard) => ({
          text: formatDate(
            savedDashboard.created_date,
            hasLocalization ? 'short' : 'iso',
          ),
        }),
      },
      {
        key: 'modified_date',
        title: intl.formatMessage({
          id: 'SavedDashboards.lastModifiedColumn',
          defaultMessage: 'Last Modified',
          description:
            'Table column header for dashboard last modification date',
        }),
        mapCellProps: (savedDashboard: SavedDashboard) => ({
          text: formatDate(
            savedDashboard.modified_date,
            hasLocalization ? 'short' : 'iso',
          ),
        }),
      },
    ],
    [],
  );
  const rowActions = useMemo(() => {
    const defaultMenu = [
      {
        label: intl.formatMessage({
          id: 'SavedDashboards.renameAction',
          defaultMessage: 'Rename',
          description: 'Row action to rename a dashboard',
        }),
        onClick: async (dashboard: SavedDashboard) => {
          trackSegment('selectRenameDashboard', {
            id: dashboard.id,
            name: dashboard.name,
            user_id: user.id,
            client_id: user.client,
          });
          dispatch(
            slices.savedDashboards.actions.setActiveModal({
              dashboard,
              name: SavedDashboardsActions.Rename,
            }),
          );
        },
        icon: '',
      },
      {
        label: intl.formatMessage({
          id: 'SavedDashboards.shareAction',
          defaultMessage: 'Share',
          description: 'Row action to share a dashboard',
        }),
        onClick: async (dashboard: SavedDashboard) => {
          trackSegment('selectShareDashboard', {
            id: dashboard.id,
            name: dashboard.name,
            user_id: user.id,
            client_id: user.client,
          });
          dispatch(
            slices.savedDashboards.actions.setActiveModal({
              dashboard,
              name: SavedDashboardsActions.Share,
            }),
          );
        },
        icon: '',
      },
      {
        label: intl.formatMessage({
          id: 'SavedDashboards.deleteAction',
          defaultMessage: 'Delete',
          description: 'Row action to delete a dashboard',
        }),
        onClick: async (dashboard: SavedDashboard) => {
          trackSegment('selectDeleteDashboard', {
            id: dashboard.id,
            name: dashboard.name,
            user_id: user.id,
            client_id: user.client,
          });
          dispatch(
            slices.savedDashboards.actions.setActiveModal({
              dashboard,
              name: SavedDashboardsActions.Delete,
            }),
          );
        },
        icon: '',
      },
    ];

    return defaultMenu;
  }, [user]);

  const onPaginate = useCallback(
    ({ pageIndex }: { pageIndex: number }) => {
      if (pageIndex !== page) {
        setPage(pageIndex);
      }
    },
    [page],
  );

  const onPageSizeChange = useCallback((pageSize: number) => {
    setPageSize(pageSize);
  }, []);

  useEffect(() => {
    if (isError) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'savedDashboards.fetchError',
          defaultMessage: 'An error occurred while fetching saved dashboards.',
          description: 'Error message when fetching saved dashboards',
        }),
      );
    }
  }, [isError]);

  useEffect(() => {
    dispatch(slices.dashboardV2.actions.reset());
  }, []);

  const noResults = !isError && data?.results.length === 0;

  return (
    <PageLayout
      title={intl.formatMessage({
        id: 'SavedDashboards.pageTitle',
        defaultMessage: 'Saved Dashboards',
        description: 'Page title for the saved dashboards page',
      })}
      loadingContent={{
        isLoading: isFetching,
        message: intl.formatMessage({
          id: 'savedDashboards.loading',
          defaultMessage: 'Loading saved dashboards…',
          description: 'Loading message for saved dashboards',
        }),
      }}
    >
      <TableToggleFilter
        options={filterOptions}
        selectedOptions={typesParam}
        onChange={setTypesParam}
      />
      {noResults ? (
        <EmptyPage preset="no-saved-dashboards" />
      ) : (
        <Layout direction="column" spacing={8} mt={6}>
          <PersistedTable
            context={TableContextType.SavedDashboards}
            name={intl.formatMessage({
              id: 'SavedDashboards.tableName',
              defaultMessage: 'Saved Dashboards',
              description: 'Name for the saved dashboards table',
            })}
            data={data?.results || []}
            totalCount={data?.total}
            columns={columns}
            getRowActions={(dashboard: SavedDashboard) => {
              return rowActions.map((rowAction) => {
                const disabled = !isCreatorOrAdmin(dashboard);
                return { ...rowAction, disabled };
              });
            }}
            options={{
              enableExportXlsx: false,
              enablePageSizeSelect: true,
              enableSelectRows: false,
            }}
            state={{
              pageIndex: page,
              pageSize,
              sortBy: [sortBy],
            }}
            onUpdate={handleUpdate}
            onPaginate={onPaginate}
            onPageSizeChange={onPageSizeChange}
            reactTableOptions={{
              autoResetSortBy: false,
              disableSortRemove: true,
              manualSortBy: true,
            }}
          />
        </Layout>
      )}

      <RenameDashboard />
      <ShareDashboard />
      <DeleteDashboard isLast={data?.total === 1} />
    </PageLayout>
  );
});

export default Page;
